import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtPct, fmtDateTime } from "@/lib/api";
import { useEntity } from "@/lib/entityStore";
import { Card } from "@/components/ds/Card";
import { PageShell, PageHeader, Section, Loading } from "@/components/ds/PageShell";
import { Stat } from "@/components/ds/Stat";
import { SeverityDot } from "@/components/ds/Severity";
import { Disclosure } from "@/components/ds/Disclosure";
import { ProductionBadge } from "@/components/ds/ProductionBadge";
import {
    LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
    CartesianGrid, BarChart, Bar,
} from "recharts";

const SEV_ORDER = { critical: 0, high: 1, medium: 2, low: 3 };

export default function EntityDashboard() {
    const { entityId } = useParams();
    const { selectEntity } = useEntity();
    const nav = useNavigate();
    const [data, setData] = useState(null);

    useEffect(() => {
        selectEntity(entityId);
        api.get(`/entities/${entityId}/dashboard`).then((r) => setData(r.data));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entityId]);

    if (!data)
        return (
            <PageShell>
                <Loading label="Loading the entity…" testId="entity-loading" />
            </PageShell>
        );

    const { entity, snapshot: s, audit_findings } = data;
    const sortedFindings = [...audit_findings].sort(
        (a, b) => (SEV_ORDER[a.severity] ?? 9) - (SEV_ORDER[b.severity] ?? 9),
    );

    const arBuckets = Object.entries(s.ar_buckets).map(([k, v]) => ({ bucket: k, amount: v }));
    const apBuckets = Object.entries(s.ap_buckets).map(([k, v]) => ({ bucket: k, amount: v }));
    const net30 = s.cash_series_30d.reduce((a, b) => a + b.net, 0);

    return (
        <PageShell width="default" testId="entity-dashboard">
            <PageHeader
                eyebrow={
                    <span className="inline-flex items-center gap-2">
                        Entity
                        <ProductionBadge state={entity.data_mode || "mock"} />
                    </span>
                }
                title={entity.display_name}
                subtitle={`${entity.legal_name} · last sync ${fmtDateTime(entity.last_sync_at)}.`}
                action={
                    <button
                        type="button"
                        onClick={() => nav("/entities")}
                        className="text-[13px] text-stone-600 hover:text-stone-900"
                        data-testid="back-entities"
                    >
                        ← All entities
                    </button>
                }
            />

            {/* Entity pulse — one calm card with the 6 numbers that matter */}
            <Card variant="default" padding="lg" className="mb-6" testId="entity-pulse">
                <div className="label-soft mb-2 text-stone-500">Entity pulse</div>
                <div className="text-display-md text-stone-900 leading-snug mb-1">
                    {s.net_profit_mtd >= 0 ? (
                        <>Profitable month so far — <span className="text-emerald-700">{fmtMoney(s.net_profit_mtd)}</span> net.</>
                    ) : (
                        <>Net loss month-to-date — <span className="text-red-700">{fmtMoney(s.net_profit_mtd)}</span>.</>
                    )}
                </div>
                <p className="text-body text-stone-500 max-w-prose">
                    Revenue MTD {fmtMoney(s.revenue_mtd)}
                    {s.revenue_mom_change_pct != null && (
                        <>
                            {" "}({s.revenue_mom_change_pct >= 0 ? "+" : ""}
                            {s.revenue_mom_change_pct.toFixed(1)}% vs prev month)
                        </>
                    )}{" "}· margin {fmtPct(s.gross_margin_mtd_pct)} · cash {fmtMoney(s.cash_position)}.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-8 gap-y-5 mt-6">
                    <Stat label="Revenue MTD" value={s.revenue_mtd} size="sm" testId="kpi-rev" />
                    <Stat label="Gross profit" value={s.gross_profit_mtd} size="sm" testId="kpi-gp" />
                    <Stat label="Net profit MTD" value={s.net_profit_mtd} size="sm" tone={s.net_profit_mtd >= 0 ? "positive" : "negative"} testId="kpi-np" />
                    <Stat label="Cash" value={s.cash_position} size="sm" testId="kpi-cash" />
                    <Stat label="Receivables" value={s.ar_total} size="sm" testId="kpi-ar" />
                    <Stat label="Payables" value={s.ap_total} size="sm" testId="kpi-ap" />
                </div>
            </Card>

            <Section
                title="Cashflow"
                subtitle={
                    net30 >= 0
                        ? `Net positive ${fmtMoney(net30)} across the last 30 days.`
                        : `Cumulative cash drop of ${fmtMoney(Math.abs(net30))} across the last 30 days.`
                }
            >
                <Card variant="default" padding="lg" testId="cash-chart">
                    <div style={{ width: "100%", height: 240, minHeight: 240 }}>
                        <ResponsiveContainer>
                            <LineChart data={s.cash_series_30d}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                                <XAxis dataKey="date" stroke="#78716c" fontSize={10} tickFormatter={(d) => d.slice(5)} />
                                <YAxis stroke="#78716c" fontSize={10} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                                <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={{ borderRadius: 6, fontSize: 12 }} />
                                <Line type="step" dataKey="cash_in" stroke="#44403c" dot={false} strokeWidth={1.5} />
                                <Line type="step" dataKey="cash_out" stroke="#a12a65" dot={false} strokeWidth={1.5} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </Card>
            </Section>

            <Section
                title="Receivables and payables"
                subtitle="Aged ladder for both sides."
            >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    <Card variant="default" padding="lg">
                        <div className="label-soft text-stone-500 mb-3">Receivables · aged</div>
                        <div style={{ width: "100%", height: 180, minHeight: 180 }}>
                            <ResponsiveContainer>
                                <BarChart data={arBuckets} layout="vertical">
                                    <XAxis type="number" stroke="#78716c" fontSize={10} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                                    <YAxis type="category" dataKey="bucket" stroke="#78716c" fontSize={11} width={60} />
                                    <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={{ borderRadius: 6, fontSize: 12 }} />
                                    <Bar dataKey="amount" fill="#44403c" radius={[0, 2, 2, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="mt-4">
                            <div className="label-soft text-stone-500 mb-2">Largest debtors</div>
                            <ul className="text-[13.5px]">
                                {s.largest_debtors.map((d, i) => (
                                    <li key={i} className="flex justify-between py-1.5 border-b border-stone-100 last:border-0">
                                        <span className="text-stone-700">{d.name}</span>
                                        <span className="font-mono-num text-stone-800">{fmtMoney(d.amount)}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Card>

                    <Card variant="default" padding="lg">
                        <div className="label-soft text-stone-500 mb-3">Payables · aged</div>
                        <div style={{ width: "100%", height: 180, minHeight: 180 }}>
                            <ResponsiveContainer>
                                <BarChart data={apBuckets} layout="vertical">
                                    <XAxis type="number" stroke="#78716c" fontSize={10} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                                    <YAxis type="category" dataKey="bucket" stroke="#78716c" fontSize={11} width={60} />
                                    <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={{ borderRadius: 6, fontSize: 12 }} />
                                    <Bar dataKey="amount" fill="#a12a65" radius={[0, 2, 2, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="mt-4">
                            <div className="label-soft text-stone-500 mb-2">Largest suppliers</div>
                            <ul className="text-[13.5px]">
                                {s.largest_suppliers.map((d, i) => (
                                    <li key={i} className="flex justify-between py-1.5 border-b border-stone-100 last:border-0">
                                        <span className="text-stone-700">{d.name}</span>
                                        <span className="font-mono-num text-stone-800">{fmtMoney(d.amount)}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Card>
                </div>
            </Section>

            <Section
                title="Findings and tax"
                subtitle="Deterministic audit results and BAS exposure for this entity."
            >
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                    <Card variant="default" padding="lg" className="lg:col-span-2" testId="entity-findings">
                        <div className="label-soft text-stone-500 mb-3">
                            Key findings ({sortedFindings.length})
                        </div>
                        {sortedFindings.length === 0 ? (
                            <div className="text-[14px] text-stone-500 py-2">No issues detected.</div>
                        ) : (
                            <>
                                <ul className="space-y-3">
                                    {sortedFindings.slice(0, 5).map((f) => (
                                        <li key={f.id} className="flex items-start gap-3">
                                            <SeverityDot level={f.severity} className="mt-1.5" />
                                            <div className="flex-1 min-w-0">
                                                <div className="text-[14px] font-medium text-stone-900 leading-snug">
                                                    {f.title}
                                                </div>
                                                <div className="text-[13px] text-stone-500 mt-1">
                                                    {f.category} · {f.recommended_action}
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                                {sortedFindings.length > 5 && (
                                    <div className="mt-4 pt-3 border-t border-stone-100">
                                        <Disclosure
                                            title={`Show all ${sortedFindings.length} findings`}
                                            testId="findings-all"
                                        >
                                            <ul className="space-y-3">
                                                {sortedFindings.slice(5).map((f) => (
                                                    <li key={f.id} className="flex items-start gap-3">
                                                        <SeverityDot level={f.severity} className="mt-1.5" />
                                                        <div className="flex-1">
                                                            <div className="text-[14px] font-medium text-stone-900 leading-snug">
                                                                {f.title}
                                                            </div>
                                                            <div className="text-[13px] text-stone-500 mt-1">
                                                                {f.category} · {f.recommended_action}
                                                            </div>
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        </Disclosure>
                                    </div>
                                )}
                            </>
                        )}
                    </Card>

                    <Card variant="default" padding="lg">
                        <div className="label-soft text-stone-500 mb-3">GST / BAS · MTD</div>
                        <div className="grid grid-cols-2 gap-3 mb-4">
                            <Stat
                                label="GST collected"
                                value={s.gst_collected_mtd}
                                size="sm"
                            />
                            <Stat label="GST paid" value={s.gst_paid_mtd} size="sm" />
                        </div>
                        <div className="pt-4 border-t border-stone-100">
                            <Stat
                                label="Net BAS exposure"
                                value={s.bas_exposure_mtd}
                                size="md"
                                tone={s.bas_exposure_mtd >= 0 ? "warning" : "positive"}
                                helper={
                                    s.bas_exposure_mtd >= 0
                                        ? "Accruing — set aside cash before quarter close"
                                        : "Refund position this month"
                                }
                            />
                        </div>
                    </Card>
                </div>
            </Section>
        </PageShell>
    );
}
